<script lang="ts">
  import { Volume2, Eye, EyeOff, CheckCircle, XCircle } from 'lucide-svelte';
  import { scale } from 'svelte/transition';
  import Button from '../ui/button.svelte';
  import type { ExerciseItem, EvaluationResult, UserAnswer, TypeInAnswer } from '../../types/exercise';
  import { speakFlashcard } from '../../lib/tts';
  import { _ } from 'svelte-i18n';

  interface Props {
    exercise: ExerciseItem;
    showFeedback: boolean;
    evaluationResult: EvaluationResult | null;
    onAnswerChange: (answer: UserAnswer) => void;
  }

  let { exercise, showFeedback, evaluationResult, onAnswerChange }: Props = $props();

  // Local state
  let userInput = $state('');
  let showHint = $state(false);
  let isSpeaking = $state(false);

  // Get mode-specific data
  let modeData = $derived(
    exercise.modeData.type === 'TYPE_IN' ? exercise.modeData : null
  );

  // Update parent when input changes
  $effect(() => {
    if (userInput.trim()) {
      const answer: TypeInAnswer = {
        type: 'TYPE_IN',
        text: userInput,
      };
      onAnswerChange(answer);
    }
  });

  // Reset state when exercise changes
  $effect(() => {
    if (exercise.id) {
      userInput = '';
      showHint = false;
    }
  });

  // Handle TTS
  async function speakPrompt() {
    if (isSpeaking) {
      isSpeaking = false;
      return;
    }

    try {
      isSpeaking = true;
      const languageCode = exercise.vocabItem.targetLanguage?.code || 'en';
      // Speak the target word if direction is target-to-primary
      const textToSpeak = exercise.direction === 'target-to-primary'
        ? exercise.vocabItem.target
        : exercise.vocabItem.primary;
      await speakFlashcard(textToSpeak, languageCode);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      isSpeaking = false;
    }
  }
</script>

<div class="space-y-5">
  <!-- Prompt -->
  <div class="text-center" in:scale={{ duration: 300, start: 0.95 }}>
    <h2 class="text-2xl font-bold mb-3 tracking-tight">{exercise.prompt}</h2>

    <!-- Hint toggle -->
    {#if modeData && (modeData.transliteration || modeData.phonetic)}
      {#if !showHint}
        <Button
          onclick={() => (showHint = true)}
          variant="ghost"
          size="sm"
          class="mt-2 text-muted-foreground hover:text-primary"
        >
          <Eye class="w-4 h-4 mr-2" />
          {$_('exercise.show_hint')}
        </Button>
      {:else}
        <div class="mt-4 p-4 bg-secondary/30 rounded-xl inline-block backdrop-blur-sm animate-in fade-in slide-in-from-top-2">
          <p class="text-lg font-medium text-secondary-foreground mb-3">
            {modeData.transliteration
              ? modeData.transliteration
              : modeData.phonetic}
          </p>
          <div class="flex items-center justify-center gap-3">
            <Button
              onclick={speakPrompt}
              variant="secondary"
              size="sm"
              class={isSpeaking ? 'bg-primary/20 text-primary animate-pulse' : ''}
            >
              <Volume2 class="w-4 h-4 mr-2" />
              {$_('buttons.listen')}
            </Button>
            <Button
              onclick={() => (showHint = false)}
              variant="ghost"
              size="sm"
            >
              <EyeOff class="w-4 h-4 mr-2" />
              {$_('exercise.hide')}
            </Button>
          </div>
        </div>
      {/if}
    {/if}
  </div>

  <!-- Input field -->
  <div>
    <input
      type="text"
      bind:value={userInput}
      disabled={showFeedback}
      placeholder={$_('exercise.enter_translation')}
      class="form-input text-xl text-center w-full p-4 rounded-xl border-2 transition-all duration-200 outline-none
             {showFeedback && evaluationResult
               ? evaluationResult.isCorrect
                 ? 'border-green-500 bg-green-50 dark:bg-green-500/30 text-green-900 dark:text-green-50'
                 : 'border-red-500 bg-red-50 dark:bg-red-500/30 text-red-900 dark:text-red-50'
               : 'border-border bg-secondary/10 focus:border-primary focus:bg-background focus:ring-4 ring-primary/10'}"
    />
  </div>

  <!-- Feedback -->
  {#if showFeedback && evaluationResult}
    <div
      in:scale={{ duration: 300, start: 0.95 }}
      class="p-5 rounded-xl shadow-lg border-2 backdrop-blur-sm
        {evaluationResult.isCorrect
        ? 'bg-green-100/80 dark:bg-green-500/30 border-green-500 text-green-900 dark:text-green-50'
        : 'bg-red-100/80 dark:bg-red-500/30 border-red-500 text-red-900 dark:text-red-50'}"
    >
      <div class="flex items-start gap-3">
        {#if evaluationResult.isCorrect}
            <div class="p-1 bg-green-200 dark:bg-green-800 rounded-full">
                <CheckCircle class="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.correct')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1 opacity-90">
                    {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <p class="opacity-90">{$_('exercise.great_job')}</p>
              {/if}
            </div>
        {:else}
            <div class="p-1 bg-red-200 dark:bg-red-800 rounded-full">
                <XCircle class="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <div>
              <h4 class="font-bold text-lg">{$_('exercise.incorrect')}</h4>
              
              {#if evaluationResult.typoDetected && evaluationResult.suggestedCorrection}
                 <p class="text-sm mt-1">
                   {$_('exercise.typo_feedback')} "{userInput}" → {$_('exercise.correct_is')} <strong>"{evaluationResult.suggestedCorrection}"</strong>
                 </p>
              {:else}
                 <span class="font-semibold text-lg block mt-1">
                    {$_('exercise.correct_answer_label')} {evaluationResult.correctAnswer}
                 </span>
              {/if}
            </div>
        {/if}
      </div>
    </div>
  {/if}
</div>
